import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Bot, Plus, Trash2, CheckCircle, FileText, Upload, Sparkles, AlertCircle, Eye, RefreshCw, Layers } from 'lucide-react';
import { useAuth } from '../components/AuthGuard';

const InputBlock = ({ label, type = 'text', value, onChange, placeholder, required = false }) => (
  <div>
    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
      {label} {required && <span className="text-[#8B1A2D]">*</span>}
    </label>
    <input
      type={type}
      required={required}
      className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#8B1A2D] focus:ring-1 focus:ring-[#8B1A2D]/30 transition-all font-medium placeholder-zinc-600"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  </div>
);

export default function AgentKnowledgeManager() {
  const { user, role: currentUserRole } = useAuth();
  const [knowledgeItems, setKnowledgeItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [isIngesting, setIsIngesting] = useState(false);

  // Form State
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('program_admissions');
  const [rawText, setRawText] = useState('');
  const [fileName, setFileName] = useState('');
  const [activeTab, setActiveTab] = useState('text'); // 'text' | 'file'
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetchKnowledge = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'ai_knowledge_base'));
      const data = [];
      snap.forEach(d => data.push({ id: d.id, ...d.data() }));
      
      // Sort newest first
      data.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setKnowledgeItems(data);
    } catch (e) {
      console.error('Error fetching AI Knowledge Base:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchKnowledge();
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    if (!title) {
      setTitle(file.name.replace(/\.[^/.]+$/, ""));
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      setRawText(evt.target.result);
    };
    reader.readAsText(file);
  };

  const handleIngest = async (e) => {
    e.preventDefault();
    if (!title.trim() || !rawText.trim()) {
      alert('Please provide a document title and text content to ingest.');
      return;
    }

    setIsIngesting(true);
    setStatusMessage('ingesting');
    
    const docId = `doc_${Date.now()}`;
    const wordCount = rawText.trim().split(/\s+/).length;

    try {
      await setDoc(doc(db, 'ai_knowledge_base', docId), {
        title: title.trim(),
        category,
        content: rawText.trim(),
        wordCount,
        source: fileName ? `Uploaded: ${fileName}` : 'Manual Entry',
        status: 'active',
        createdAt: new Date().toISOString(),
        createdBy: user?.email || 'Director / SuperAdmin',
        vectorStatus: 'indexed'
      });

      setTitle('');
      setRawText('');
      setFileName('');
      setStatusMessage('success');
      fetchKnowledge();
      setTimeout(() => setStatusMessage(''), 3500);
    } catch (err) {
      console.error('Failed to ingest document:', err);
      setStatusMessage('error');
      alert('Failed to ingest document into AI Knowledge Base. Check permissions.');
    }
    setIsIngesting(false);
  };

  const toggleStatus = async (docId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    try {
      await updateDoc(doc(db, 'ai_knowledge_base', docId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      fetchKnowledge();
    } catch (err) {
      console.error('Error updating document status:', err);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this knowledge document from the AI agent memory?')) return;
    try {
      await deleteDoc(doc(db, 'ai_knowledge_base', docId));
      fetchKnowledge();
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#8B1A2D]/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="p-2.5 bg-[#8B1A2D]/20 text-[#8B1A2D] border border-[#8B1A2D]/30 rounded-2xl">
                <Bot size={22} />
              </span>
              <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                Executive Access Only
              </span>
            </div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight">
              AI Agent Knowledge Hub
            </h1>
            <p className="text-zinc-400 text-xs font-bold max-w-xl leading-relaxed">
              Dynamically train and update ecosystem AI agents in real-time. Ingest internal guidelines, course descriptions, fee updates, or policy docs without code redeployments.
            </p>
          </div>

          <div className="flex items-center gap-3 bg-zinc-900/90 border border-zinc-800 p-4 rounded-2xl">
            <div className="text-right">
              <div className="text-xs font-black text-zinc-400 uppercase tracking-wider">Active Memory Corpus</div>
              <div className="text-2xl font-black text-[#8B1A2D]">
                {knowledgeItems.filter(k => k.status === 'active').length} <span className="text-xs text-zinc-500 font-bold">Docs</span>
              </div>
            </div>
            <Sparkles className="text-amber-400 ml-2" size={24} />
          </div>
        </div>
      </div>

      {/* Ingestion Panel */}
      <div className="bg-zinc-950 border border-zinc-800/80 rounded-3xl p-6 shadow-xl space-y-6">
        <div className="flex items-center justify-between border-b border-zinc-800/80 pb-4">
          <div className="flex items-center gap-2">
            <Layers className="text-[#8B1A2D]" size={18} />
            <h2 className="text-sm font-black text-white uppercase tracking-wider">Ingest New Knowledge Item</h2>
          </div>
          <div className="flex bg-zinc-900 p-1 rounded-xl border border-zinc-800">
            <button
              onClick={() => setActiveTab('text')}
              className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                activeTab === 'text' ? 'bg-[#8B1A2D] text-white shadow-md' : 'text-zinc-400 hover:text-white'
              }`}
            >
              Direct Text
            </button>
            <button
              onClick={() => setActiveTab('file')}
              className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                activeTab === 'file' ? 'bg-[#8B1A2D] text-white shadow-md' : 'text-zinc-400 hover:text-white'
              }`}
            >
              Document Upload
            </button>
          </div>
        </div>

        <form onSubmit={handleIngest} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputBlock
              label="Document Title / Descriptor"
              placeholder="e.g. SUNY 2026 Accreditation & Course Catalog"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />

            <div>
              <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
                Category Domain <span className="text-[#8B1A2D]">*</span>
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#8B1A2D] transition-all font-medium cursor-pointer"
              >
                <option value="program_admissions">Program Admissions & Requirements</option>
                <option value="tuition_scholarships">Tuition, Grants & Scholarships</option>
                <option value="accreditation_partners">Accreditation & University Partners</option>
                <option value="curriculum_courses">Curriculum & Course Outlines</option>
                <option value="general_faq">General Ecosystem FAQ</option>
                <option value="executive_directives">Executive Directives & Policies</option>
              </select>
            </div>
          </div>

          {activeTab === 'file' ? (
            <div className="border-2 border-dashed border-zinc-800 hover:border-[#8B1A2D]/50 bg-zinc-900/50 rounded-2xl p-8 text-center transition-colors">
              <Upload className="mx-auto text-zinc-500 mb-3" size={32} />
              <p className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-1">
                Click or Drag & Drop File to Extract Text
              </p>
              <p className="text-[10px] text-zinc-500 font-medium mb-4">Supports .txt, .md, .json, .csv files</p>
              <input
                type="file"
                accept=".txt,.md,.json,.csv"
                onChange={handleFileUpload}
                className="hidden"
                id="kb-file-upload"
              />
              <label
                htmlFor="kb-file-upload"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-black uppercase tracking-wider rounded-xl cursor-pointer transition-colors"
              >
                <FileText size={14} /> Select Document
              </label>
              {fileName && (
                <div className="mt-4 inline-block px-3 py-1 bg-[#8B1A2D]/20 border border-[#8B1A2D]/40 text-amber-300 text-xs font-bold rounded-lg">
                  Extracted from: {fileName} ({rawText.split(/\s+/).length} words)
                </div>
              )}
            </div>
          ) : null}

          <div>
            <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
              Document Text Knowledge Content <span className="text-[#8B1A2D]">*</span>
            </label>
            <textarea
              required
              rows={6}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder="Paste raw text, course descriptions, fee schedules, or standard responses that AI agents should memorize..."
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl p-4 focus:outline-none focus:border-[#8B1A2D] focus:ring-1 focus:ring-[#8B1A2D]/30 transition-all font-mono placeholder-zinc-600 leading-relaxed"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2">
              {statusMessage === 'success' && (
                <span className="flex items-center gap-1.5 text-emerald-400 text-xs font-black uppercase tracking-wider">
                  <CheckCircle size={14} /> Knowledge Ingested & Synced
                </span>
              )}
              {statusMessage === 'error' && (
                <span className="flex items-center gap-1.5 text-red-400 text-xs font-black uppercase tracking-wider">
                  <AlertCircle size={14} /> Ingestion Failed
                </span>
              )}
            </div>

            <button
              type="submit"
              disabled={isIngesting}
              className="flex items-center gap-2 px-8 py-3 bg-[#8B1A2D] hover:bg-[#a61f37] text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-[#8B1A2D]/20 disabled:opacity-50 cursor-pointer"
            >
              {isIngesting ? (
                <>
                  <RefreshCw size={14} className="animate-spin" /> Ingesting Corpus...
                </>
              ) : (
                <>
                  <Sparkles size={14} /> Ingest into AI Memory
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Active Knowledge Base Corpus Table */}
      <div className="bg-zinc-950 border border-zinc-800/80 rounded-3xl p-6 shadow-xl space-y-4">
        <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-2">
            <Bot className="text-amber-400" size={18} />
            <h2 className="text-sm font-black text-white uppercase tracking-wider">
              Ingested Knowledge Corpus ({knowledgeItems.length})
            </h2>
          </div>
          <button
            onClick={fetchKnowledge}
            className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors"
            title="Refresh Knowledge"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-zinc-500 font-bold text-xs uppercase tracking-widest animate-pulse">
            Loading Knowledge Base Corpus...
          </div>
        ) : knowledgeItems.length === 0 ? (
          <div className="py-12 text-center text-zinc-500 font-bold text-xs uppercase tracking-widest">
            No dynamic knowledge documents ingested yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-zinc-800 text-[9px] font-black uppercase tracking-widest text-zinc-400">
                  <th className="py-3 px-4">Document</th>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4">Words</th>
                  <th className="py-3 px-4">Ingested At</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60 text-xs">
                {knowledgeItems.map((item) => (
                  <tr key={item.id} className="hover:bg-zinc-900/50 transition-colors">
                    <td className="py-3.5 px-4 font-bold text-white max-w-xs truncate">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-[#8B1A2D] shrink-0" />
                        <span className="truncate">{item.title}</span>
                      </div>
                      <div className="text-[10px] text-zinc-500 font-normal truncate mt-0.5">
                        {item.source || 'Manual Entry'} • By {item.createdBy}
                      </div>
                    </td>

                    <td className="py-3.5 px-4 text-zinc-400 font-medium capitalize">
                      {item.category ? item.category.replace('_', ' ') : 'General'}
                    </td>

                    <td className="py-3.5 px-4 font-mono text-zinc-300">
                      {item.wordCount || item.content?.split(/\s+/).length || 0}
                    </td>

                    <td className="py-3.5 px-4 text-zinc-400 text-[11px]">
                      {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'N/A'}
                    </td>

                    <td className="py-3.5 px-4 text-center">
                      <button
                        onClick={() => toggleStatus(item.id, item.status)}
                        className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border transition-all cursor-pointer ${
                          item.status === 'active'
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'
                            : 'bg-zinc-800 text-zinc-500 border-zinc-700 hover:text-white'
                        }`}
                      >
                        {item.status === 'active' ? 'Active' : 'Disabled'}
                      </button>
                    </td>

                    <td className="py-3.5 px-4 text-right space-x-2">
                      <button
                        onClick={() => setPreviewDoc(item)}
                        className="p-1.5 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-lg transition-colors"
                        title="Preview Text"
                      >
                        <Eye size={13} />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 text-zinc-500 hover:text-red-400 bg-zinc-900 border border-zinc-800 rounded-lg transition-colors"
                        title="Delete Document"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded-3xl max-w-2xl w-full p-6 space-y-4 shadow-2xl animate-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <h3 className="text-sm font-black text-white uppercase tracking-wider truncate">
                {previewDoc.title}
              </h3>
              <button
                onClick={() => setPreviewDoc(null)}
                className="text-zinc-500 hover:text-white text-xs font-black uppercase tracking-wider"
              >
                Close
              </button>
            </div>
            <div className="bg-zinc-900 p-4 rounded-xl max-h-96 overflow-y-auto font-mono text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">
              {previewDoc.content}
            </div>
            <div className="flex justify-end pt-2">
              <button
                onClick={() => setPreviewDoc(null)}
                className="px-5 py-2 bg-[#8B1A2D] text-white text-xs font-black uppercase tracking-wider rounded-xl"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
